import React, { useCallback, useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { graphql, useFragment } from "react-relay"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./PhaseAndRoleEditor.module.css"

import { PhaseAndRoleEditor_project$key } from "./__generated__/PhaseAndRoleEditor_project.graphql"

import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import Column from "../TableColumn"
import Row from "../TableRow"
import { ProjectBudgetFormData } from "../formTypes"
import {
  calculatePhaseTotalBudget,
  calculatePhaseTotalOtherExpenses,
} from "../projectBudgetHelpers"
import { RateCard } from "../types"

import PhaseRoleSection from "./PhaseRoleSection"
import TotalsRow from "./TotalsRow"

type Props = {
  project: PhaseAndRoleEditor_project$key
  rateCard: RateCard
  defaultFullTimeMinutes: number
}

const PhaseAndRoleEditor: React.FC<Props> = (props) => {
  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()

  const { showFinancialColumns, extractedRoles } = useProjectBudgetFormContext()

  const project = useFragment<PhaseAndRoleEditor_project$key>(
    graphql`
      fragment PhaseAndRoleEditor_project on projects {
        id
        phases {
          id
          name
          color
          start_date: start_date_runn
          phase_roles {
            id
            role {
              id
              name
            }
            estimated_minutes
          }
        }
        project_roles {
          id
          role_id
          estimated_minutes
        }
        members {
          id
          role_id
        }
      }
    `,
    props.project,
  )

  // `.people` is an artifact of a historical type definition. It's actually
  // the roles, not people. Yes, this is confusing
  const allRoles = extractedRoles.people

  const totalMinutes = formData.phaseAndRoleBudgets.reduce(
    (sum, budget) => sum + budget.estimatedMinutes,
    0,
  )

  // Helper function for rendering all the roles for a given phase
  const getPhaseRoles = useCallback(
    (phaseId: number) => {
      return formData.phaseAndRoleBudgets.filter(
        (budget) => budget.phaseId === phaseId,
      )
    },
    [formData.phaseAndRoleBudgets],
  )

  const totalAllocated = project.phases.reduce((sum, phase) => {
    const phaseRoles = getPhaseRoles(phase.id)
    const phaseBudget = calculatePhaseTotalBudget(
      phaseRoles,
      props.rateCard,
      formData.projectRates,
    )
    return sum + phaseBudget
  }, 0)
  const totalOtherExpenses = calculatePhaseTotalOtherExpenses(
    formData.otherExpenses,
  )

  const getUnallocatedRolesForPhase = useCallback(
    (phaseId: number) => {
      const allocatedRoleIds = formData.phaseAndRoleBudgets
        .filter((budget) => budget.phaseId === phaseId)
        .map((budget) => budget.roleId)

      return allRoles.filter((role) => !allocatedRoleIds.includes(role.id))
    },
    [formData.phaseAndRoleBudgets, allRoles],
  )

  const getPhaseExpenses = useCallback(
    (phaseId: number) => {
      return formData.otherExpenses.filter(
        (expense) => expense.phaseId === phaseId,
      )
    },
    [formData.otherExpenses],
  )

  const handleAddRoleToPhase = (phaseId: number, roleId: number) => {
    const newBudget = {
      phaseId,
      roleId,
      projectId: project.id,
      estimatedMinutes: 0,
    }
    setValue("phaseAndRoleBudgets", [
      ...formData.phaseAndRoleBudgets,
      newBudget,
    ])
  }

  const handleShowAllRolesForPhase = (phaseId: number) => {
    const unallocatedRoles = getUnallocatedRolesForPhase(phaseId)
    const newBudgets = unallocatedRoles.map((role) => ({
      phaseId,
      roleId: role.id,
      projectId: project.id,
      estimatedMinutes: 0,
    }))
    setValue("phaseAndRoleBudgets", [
      ...formData.phaseAndRoleBudgets,
      ...newBudgets,
    ])
  }

  const orderedPhases = useMemo(() => {
    return [...project.phases].sort((a, b) => {
      return Number(a.start_date) - Number(b.start_date)
    })
  }, [project.phases])

  return (
    <div
      className={commonStyles.budgetEditorWrapper}
      data-test="phase-and-role-editor"
    >
      <Row heading>
        <Column>Phases</Column>
        <Column />
        <Column />
        {showFinancialColumns && (
          <Column className={commonStyles.alignRight}>Phase Budget</Column>
        )}
      </Row>

      <div className={styles.tableBody}>
        {orderedPhases.map((phase) => {
          const phaseRoles = getPhaseRoles(phase.id)
          const phaseExpenses = getPhaseExpenses(phase.id)
          const unallocatedRoles = getUnallocatedRolesForPhase(phase.id)

          return (
            <PhaseRoleSection
              key={phase.id}
              phase={phase}
              phaseRoles={phaseRoles}
              phaseExpenses={phaseExpenses}
              unallocatedRoles={unallocatedRoles}
              onAddRole={handleAddRoleToPhase}
              onShowAllRoles={handleShowAllRolesForPhase}
              rateCard={props.rateCard}
              defaultFullTimeMinutes={props.defaultFullTimeMinutes}
            />
          )
        })}
      </div>

      <TotalsRow
        totalOtherExpenses={totalOtherExpenses}
        totalMinutes={totalMinutes}
        totalAllocated={totalAllocated}
        defaultFullTimeMinutes={props.defaultFullTimeMinutes}
      />
    </div>
  )
}

export default PhaseAndRoleEditor
